import { Box, FileText, Headphones, Image, Video } from 'lucide-react'; // Import icons, replaced Cube with Box
import { useNavigate } from 'react-router';
import { Card } from '../../../components/atoms/Card';
import { Exhibit } from '../../../types/models';
import { cn } from '../../../utils/cn';

interface RelatedExhibitsProps {
    exhibits: Exhibit[];
    roomId: number;
    className?: string;
    languageCode?: string;
}

/**
 * Related exhibits component for showing exhibits linked to a room
 *
 * Features:
 * - Horizontal scrollable list of exhibits
 * - Clickable exhibit cards for navigation
 * - Responsive design
 * - Empty state handling
 */
const RelatedExhibits = ({ exhibits, roomId, className, languageCode = 'en' }: RelatedExhibitsProps) => {
    const navigate = useNavigate();

    // If no exhibits are available, show empty state
    if (!exhibits || exhibits.length === 0) {
        return (
            <div className={cn('rounded-lg bg-gray-50 p-6 text-center dark:bg-gray-800', className)}>
                <div className="mx-auto mb-3 h-12 w-12 text-gray-400">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect>
                        <circle cx="9" cy="9" r="2"></circle>
                        <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
                    </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">No exhibits</h3>
                <p className="mt-1 text-gray-500 dark:text-gray-400">This room doesn't have any exhibits associated with it yet.</p>
            </div>
        );
    }

    // Navigate to exhibit detail with room context
    const handleExhibitClick = (exhibitId: number) => {
        navigate(`/exhibits/${exhibitId}`, {
            state: {
                fromRoom: true,
                roomId: roomId,
            },
        });
    };

    return (
        <div className={cn('', className)}>
            <h2 className="mb-4 text-xl font-bold text-gray-900 dark:text-white">Related Exhibits</h2>

            {/* Scrollable horizontal list */}
            <div className="hide-scrollbar -mx-4 flex snap-x snap-mandatory overflow-x-auto scroll-smooth px-4 pb-4">
                {exhibits.map((exhibit) => {
                    // Get default language translation or first available
                    // Use current language or fall back to default language or first available translation
                    const translation = exhibit.translations.find((t) => t.language_code === languageCode) || exhibit.translations[0];

                    return (
                        <Card
                            key={exhibit.id}
                            as="div"
                            className="mr-4 w-72 flex-shrink-0 cursor-pointer snap-start overflow-hidden"
                            variant="elevated"
                            padding="none"
                            hoverable
                            onClick={() => handleExhibitClick(exhibit.id)}
                        >
                            {/* Exhibit image */}
                            <div className="relative aspect-video w-full overflow-hidden bg-gray-100 dark:bg-gray-800">
                                {exhibit.thumbnail_url ? (
                                    <img
                                        src={'/storage/' + exhibit.thumbnail_url}
                                        alt={translation.title}
                                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                                        loading="lazy"
                                    />
                                ) : (
                                    <div className="flex h-full w-full items-center justify-center text-gray-400 dark:text-gray-600">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="40"
                                            height="40"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M4 22h16a2 2 0 0 0 2-2V7.5L14.5 2H6a2 2 0 0 0-2 2v4"></path>
                                            <polyline points="14 2 14 8 20 8"></polyline>
                                            <path d="M3 15h6"></path>
                                            <path d="M6 18V12"></path>
                                        </svg>
                                    </div>
                                )}

                                {/* Media Type Icons */}
                                <div className="absolute top-2 right-2 flex space-x-1">
                                    {/* 3D Model Icon */}
                                    {exhibit.model_url && (
                                        <span className="rounded bg-black/50 p-0.5 text-white" title="3D Model">
                                            <Box className="h-4 w-4" /> {/* Replaced Cube with Box */}
                                        </span>
                                    )}
                                    {/* Video Icon */}
                                    {exhibit.video_url && (
                                        <span className="rounded bg-black/50 p-0.5 text-white" title="Video">
                                            <Video className="h-4 w-4" />
                                        </span>
                                    )}
                                    {/* Audio Guide Icon */}
                                    {exhibit.audio_guide_url && (
                                        <span className="rounded bg-black/50 p-0.5 text-white" title="Audio Guide">
                                            <Headphones className="h-4 w-4" />
                                        </span>
                                    )}
                                    {/* Gallery/Photos Icon */}
                                    {exhibit.gallery_urls && exhibit.gallery_urls.length > 0 && (
                                        <span className="rounded bg-black/50 p-0.5 text-white" title="Gallery">
                                            <Image className="h-4 w-4" />
                                        </span>
                                    )}
                                    {/* Documents Icon */}
                                    {exhibit.documents_urls && exhibit.documents_urls.length > 0 && (
                                        <span className="rounded bg-black/50 p-0.5 text-white" title="Documents">
                                            <FileText className="h-4 w-4" />
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Exhibit info */}
                            <div className="p-4">
                                <h3 className="line-clamp-1 font-medium text-gray-900 dark:text-white">{translation?.title || 'Unnamed Exhibit'}</h3>

                                {translation?.description && (
                                    <p className="mt-1 line-clamp-2 text-sm text-gray-600 dark:text-gray-300">{translation.description}</p>
                                )}

                                {/* View more link */}
                                <div className="text-primary mt-2 flex items-center text-sm">
                                    View exhibit
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="ml-1"
                                    >
                                        <polyline points="9 18 15 12 9 6"></polyline>
                                    </svg>
                                </div>
                            </div>
                        </Card>
                    );
                })}
            </div>
        </div>
    );
};

export default RelatedExhibits;
